import { db } from '../../../../config/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const RECOMMENDATIONS_DOC_ID = 'projectsAndSkills';

const normalizeText = (value = '') => String(value || '').trim();
const normalizeSteps = (value) => (Array.isArray(value) ? value : [])
  .slice(0, 6)
  .map((step) => normalizeText(step).slice(0, 220))
  .filter(Boolean);

const normalizeRecommendations = (data = {}) => {
  const safeProjects = Array.isArray(data?.projects)
    ? data.projects
        .map((item) => ({
          title: normalizeText(item?.title),
          overview: normalizeText(item?.overview),
          why: normalizeText(item?.why),
          steps: normalizeSteps(item?.steps),
          outcome: normalizeText(item?.outcome).slice(0, 300),
        }))
        .filter((item) => item.title && item.overview)
    : [];

  const safeSkills = Array.isArray(data?.skills)
    ? data.skills
        .map((item) => ({
          name: normalizeText(item?.name),
          description: normalizeText(item?.description),
          why: normalizeText(item?.why),
        }))
        .filter((item) => item.name)
    : [];

  return {
    projects: safeProjects,
    skills: safeSkills,
  };
};

const dedupeProjects = (projects = []) => {
  const seen = new Set();

  return projects.filter((item) => {
    const key = `${item.title.toLowerCase()}|${item.overview.toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const dedupeSkills = (skills = []) => {
  const seen = new Set();

  return skills.filter((item) => {
    const key = item.name.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const loadAIRecommendations = async (userId) => {
  if (!userId) {
    return {
      projects: [],
      skills: [],
      updatedAt: null,
    };
  }

  const recommendationRef = doc(db, 'users', userId, 'aiRecommendations', RECOMMENDATIONS_DOC_ID);
  const recommendationSnap = await getDoc(recommendationRef);

  if (!recommendationSnap.exists()) {
    return {
      projects: [],
      skills: [],
      updatedAt: null,
    };
  }

  const data = recommendationSnap.data() || {};
  const normalized = normalizeRecommendations(data);

  return {
    ...normalized,
    updatedAt: data?.updatedAt || null,
  };
};

export const saveAIRecommendations = async (userId, recommendations, { append = true } = {}) => {
  if (!userId) {
    throw new Error('Missing userId while saving AI recommendations.');
  }

  const recommendationRef = doc(db, 'users', userId, 'aiRecommendations', RECOMMENDATIONS_DOC_ID);
  const incoming = normalizeRecommendations(recommendations);

  let nextProjects = incoming.projects;
  let nextSkills = incoming.skills;
  let createdAt = new Date();

  if (append) {
    const current = await loadAIRecommendations(userId);

    nextProjects = dedupeProjects([...(current.projects || []), ...incoming.projects]);
    nextSkills = dedupeSkills([...(current.skills || []), ...incoming.skills]);
    createdAt = current.updatedAt || createdAt;
  }

  await setDoc(
    recommendationRef,
    {
      projects: nextProjects,
      skills: nextSkills,
      updatedAt: new Date(),
      createdAt,
    },
    { merge: true }
  );

  return {
    projects: nextProjects,
    skills: nextSkills,
  };
};

export const removeAIRecommendation = async (userId, { type, title, name } = {}) => {
  if (!userId) {
    throw new Error('Missing userId while removing an AI recommendation.');
  }

  const current = await loadAIRecommendations(userId);
  const projectTitle = normalizeText(title).toLowerCase();
  const skillName = normalizeText(name).toLowerCase();
  const next = {
    projects: type === 'project' && projectTitle
      ? current.projects.filter((item) => item.title.toLowerCase() !== projectTitle)
      : current.projects,
    skills: type === 'skill' && skillName
      ? current.skills.filter((item) => item.name.toLowerCase() !== skillName)
      : current.skills,
  };

  return saveAIRecommendations(userId, next, { append: false });
};
